import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import { k8sResourceService, type K8sResource } from '@/services/k8sResourceService'
import LoadingOverlay from '@/components/ui/LoadingOverlay'
import ErrorDisplay from '@/components/ui/ErrorDisplay'
import { HttpError } from '@/lib/httpError'
import UpdateLicenseModal, { type UpdateLicenseModalRef } from '@/components/modals/UpdateLicenseModal'

export default function PlansPage() {
  const { t } = useTranslation(['plans', 'common'])
  const { refreshAuth, user } = useAuth()
  const [licenses, setLicenses] = useState<K8sResource[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const updateLicenseModalRef = useRef<UpdateLicenseModalRef>(null)

  // Permission checks
  const canDirectUpdate = user?.platform_role === 'owner'
  const canRequestUpdate = user?.platform_role === 'owner' || user?.platform_role === 'admin'

  const fetchLicenses = async () => {
    setLoading(true)
    setError('')

    // Ensure minimum 2 seconds loading time for better UX
    const startTime = Date.now()

    try {
      const response = await k8sResourceService.getLicenses()
      const elapsed = Date.now() - startTime
      const remainingTime = Math.max(0, 2000 - elapsed)

      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime))
      }

      setLicenses(response.items)
      setError('')
    } catch (err) {
      // Handle 401 error: try to refresh token
      if (err instanceof HttpError && err.status === 401) {
        try {
          await refreshAuth()
          // Retry fetching licenses after successful refresh
          return fetchLicenses()
        } catch (refreshError) {
          // Refresh failed, ensure at least 2 seconds have passed before showing error
          const elapsed = Date.now() - startTime
          const remainingTime = Math.max(0, 2000 - elapsed)

          if (remainingTime > 0) {
            await new Promise(resolve => setTimeout(resolve, remainingTime))
          }

          const refreshErrorMessage = refreshError instanceof Error ? refreshError.message : 'errors.sessionExpired'
          if (refreshErrorMessage.startsWith('errors.')) {
            setError(t(refreshErrorMessage, { ns: 'common', defaultValue: refreshErrorMessage }))
          } else {
            setError(refreshErrorMessage)
          }
          setLoading(false)
          return
        }
      }

      // For other errors, ensure at least 2 seconds have passed before showing error
      const elapsed = Date.now() - startTime
      const remainingTime = Math.max(0, 2000 - elapsed)

      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime))
      }

      const errorMessage = err instanceof Error ? err.message : 'errors.operationFailed'
      if (errorMessage.startsWith('errors.')) {
        setError(t(errorMessage, { ns: 'common', defaultValue: errorMessage }))
      } else {
        setError(errorMessage)
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchLicenses()
  }, [])

  const getStatusColor = (license: K8sResource) => {
    const phase = license.status?.phase
    const isValid = license.status?.isValid
    if (!isValid || phase !== 'Valid') return 'bg-red-500'

    const daysUntilExpiration = license.status?.daysUntilExpiration || 0
    if (daysUntilExpiration <= 30 && daysUntilExpiration > 0) return 'bg-yellow-500'
    return 'bg-green-500'
  }

  const getUsageColor = (license: K8sResource) => {
    const currentFlowCount = license.status?.currentFlowCount || 0
    const maxFlows = license.status?.maxFlows || 1
    const percentage = (currentFlowCount / maxFlows) * 100

    if (percentage >= 90) return 'bg-red-500'
    if (percentage >= 80) return 'bg-yellow-500'
    return 'bg-primary'
  }

  const getUsagePercentage = (license: K8sResource) => {
    const currentFlowCount = license.status?.currentFlowCount || 0
    const maxFlows = license.status?.maxFlows || 1
    return Math.round((currentFlowCount / maxFlows) * 100)
  }

  const formatExpiryDate = (expiresAt: string) => {
    try {
      return new Date(expiresAt).toLocaleDateString()
    } catch {
      return 'Invalid Date'
    }
  }

  return (
    <div className="absolute inset-0">
      {/* Full Screen Loading Overlay */}
      {loading && !error && <LoadingOverlay message={t('loading', 'Loading plans...')} fullScreen />}

      {/* Full Screen Error Display */}
      {error && !loading && (
        <ErrorDisplay
          error={error}
          title={t('errors.title', 'Error')}
          onRefresh={() => fetchLicenses()}
          fullScreen
        />
      )}

      {/* Content Container */}
      <div className="max-w-7xl mx-auto px-6 py-8 h-full flex flex-col overflow-y-auto">
        {/* Header */}
        <div className="mb-8 shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-text-dark">{t('title', 'Plans Management')}</h1>
            <p className="text-gray-600 mt-2">{t('description', 'Manage your plans and usage')}</p>
          </div>
          <button
            onClick={() => fetchLicenses()}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>{t('actions.refresh', 'Refresh')}</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-auto relative">

        {/* Empty State */}
        {!loading && !error && licenses.length === 0 && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('empty.title', 'No Plans Found')}</h3>
              <p className="text-gray-600">{t('empty.description', 'No plan information available')}</p>
            </div>
          </div>
        )}

        {/* License Cards */}
        {!loading && !error && licenses.length > 0 && (
          <div className="grid grid-cols-1 gap-8 pb-6 mx-auto max-w-4xl w-full">
            {licenses.map((license) => (
              <div key={license.metadata.name} className="bg-white rounded-xl shadow-lg border border-gray-200 p-7 hover:shadow-xl transition-shadow duration-300">
                {/* License Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-semibold text-text-dark">{license.metadata.name}</h3>
                    <p className="text-sm text-gray-500 capitalize">{license.status?.licenseType || 'standard'} {t('license.plan', 'Plan')}</p>
                  </div>
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-white ${getStatusColor(license)}`}>
                    <div className="w-2 h-2 bg-white rounded-full mr-2"></div>
                    {license.status?.phase || 'Unknown'}
                  </div>
                </div>

                {/* Customer Info */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-text-dark mb-2">{t('license.customer', 'Customer')}</h4>
                  <div className="text-sm text-gray-600">
                    <p><span className="font-medium">{t('license.name', 'Name')}:</span> {license.status?.customerInfo?.name || 'N/A'}</p>
                    <p><span className="font-medium">{t('license.email', 'Email')}:</span> {license.status?.customerInfo?.email || 'N/A'}</p>
                  </div>
                </div>

                {/* Usage Stats */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-text-dark">{t('license.flowUsage', 'Instance Usage')}</span>
                    <span className="text-sm text-gray-600">
                      {license.status?.currentFlowCount || 0} / {license.status?.maxFlows || 0} {t('license.instances', 'instances')}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${getUsageColor(license)}`}
                      style={{ width: `${getUsagePercentage(license)}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {getUsagePercentage(license)}% {t('license.used', 'used')}
                  </p>
                </div>

                {/* Expiry Info */}
                <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-blue-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-blue-800">
                        {t('license.expires', 'Expires')}: {formatExpiryDate(license.status?.expiresAt || '')}
                      </p>
                      <p className="text-xs text-blue-600">
                        {license.status?.daysUntilExpiration || 0} {t('license.daysLeft', 'days left')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Warning Messages */}
                {(license.status?.daysUntilExpiration || 0) <= 30 && (license.status?.daysUntilExpiration || 0) > 0 && (
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex">
                      <svg className="w-5 h-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm text-yellow-800">{t('license.expiringSoon', 'License expiring soon')}</p>
                    </div>
                  </div>
                )}

                {((license.status?.currentFlowCount || 0) / (license.status?.maxFlows || 1)) >= 0.8 && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex">
                      <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm text-red-800">{t('license.flowLimitWarning', 'Flow limit reached')}</p>
                    </div>
                  </div>
                )}

                {/* Contact Information */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-start gap-3 text-sm text-gray-600">
                      <svg className="w-5 h-5 text-gray-400 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p>
                        {canRequestUpdate
                          ? t('license.contactInfoAdmin')
                          : t('license.contactInfoUser')}
                      </p>
                    </div>
                    {canRequestUpdate && (
                      <button
                        onClick={() => updateLicenseModalRef.current?.open(license)}
                        className="px-4 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2 shrink-0"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        <span>{t('actions.updateLicense')}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>

      {/* Update License Modal */}
      <UpdateLicenseModal
        ref={updateLicenseModalRef}
        canDirectUpdate={canDirectUpdate}
        canRequestUpdate={canRequestUpdate}
        onSuccess={() => {
          fetchLicenses()
        }}
      />
    </div>
  )
}
